import React, { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../../api/axios';
import Header from '../layout/Header';
import { validateEmail, containsEmoji } from '../../../utils/Utils';
import { RESPONDENT_FORM_STEP, RELATIONSHIP_OPTIONS, getRelationshipString } from '../../../utils/Constants';
import { ApiError } from '../../../types/errors';

interface RespondentInfo {
  name: string;
  relationship: number;
  email: string;
}

interface FormErrors {
  name?: string;
  relationship?: string;
  email?: string;
  terms?: string;
}

const RespondentVerification: React.FC = () => {
  const { token } = useParams<{ token?: string }>();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState<number>(RESPONDENT_FORM_STEP.INPUT);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [agreeToTerms, setAgreeToTerms] = useState<boolean>(false);
  const [isConflict, setIsConflict] = useState<boolean>(false);

  const [respondentInfo, setRespondentInfo] = useState<RespondentInfo>({
    name: '',
    relationship: 0,
    email: ''
  });

  const [candidateInfo, setCandidateInfo] = useState<any>(null);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      navigate('/404');
    }
  }, [token]);

  const verifyToken = async (): Promise<void> => {
    try {
      setLoading(true);

      const response = await axios.get(`/respondent/verify-token/${token}`);
      setCandidateInfo(response.data.candidate);
      setRespondentInfo({
        name: response.data.respondent.name || '',
        relationship: response.data.respondent.relationship || '',
        email: response.data.respondent.email || ''
      });

      if (response.data.respondent.is_verified) {
        // 既に本人確認済みの場合、完了画面を表示する。
        //setCurrentStep(RESPONDENT_FORM_STEP.COMPLETED);

        // 既に本人確認済みの場合、回答入力画面に遷移する。
        navigate(`/respondent/form/${token}`);
      }

      setLoading(false);
    } catch (error: unknown) {
      const axiosError = error as ApiError;
      console.error('Error verify token:', axiosError);

      if (axiosError.response) {
        console.error('Error response:', axiosError.response.status, axiosError.response.data);
      }

      if (axiosError.response && axiosError.response.status === 404) {
        navigate('/404');
        return;
      }

      if (axiosError.response && axiosError.response.status == 409) {
        // 既に回答情報を登録した場合
        navigate(`/respondent/form/${token}`);
        //setIsConflict(true);
        //setLoading(false);
        return;
      }

      if (axiosError.response && axiosError.response.status == 429) {
        // 回答上限を超えた場合、完了画面を表示する。
        setIsConflict(true);
        setLoading(false);
        return;
      }

      setError('トークン検証中にエラーが発生しました。');
      setLoading(false);
    }
  };

  const handleInputChange = (e: any): void => {
    const { name, value } = e.target;
    setRespondentInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const isSameWithCandidate = (respondentEmail: string): boolean => {
    respondentEmail.trim();
    return respondentEmail.toLowerCase() === candidateInfo.email.toLowerCase();
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!respondentInfo.name.trim()) {
      newErrors.name = '氏名を入力してください。';
      isValid = false;
    } else if (containsEmoji(respondentInfo.name)) {
      newErrors.name = '絵文字は使用できません。';
      isValid = false;
    }

    if (!respondentInfo.relationship) {
      newErrors.relationship = '関係性を選択してください';
      isValid = false;
    }

    if (!respondentInfo.email.trim()) {
      newErrors.email = 'メールアドレスを入力してください。';
    } else if (!validateEmail(respondentInfo.email)) {
      newErrors.email = '不正なメールアドレスです。';
      isValid = false;
    } else if (isSameWithCandidate(respondentInfo.email)) {
      newErrors.email = '候補者のメールアドレスです。';
      isValid = false;
    } else if (containsEmoji(respondentInfo.email)) {
      newErrors.email = '絵文字は使用できません。';
      isValid = false;
    }

    if (!agreeToTerms) {
      newErrors.terms = '利用規約とプライバシーポリシーに同意してください';
      isValid = false;
    }

    setErrors(newErrors);
    setError(''); // Clear the general error

    return isValid;
  };

  const handleConfirm = (e: FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    if (e.type === 'submit') {
      e.preventDefault();
    }

    if (validateForm()) {
      setCurrentStep(RESPONDENT_FORM_STEP.CONFIRM);
    }
  };

  const handleBack = (): void => {
    setCurrentStep(RESPONDENT_FORM_STEP.INPUT);
  };

  const handleSubmit = async (): Promise<void> => {
    setLoading(true);

    try {
      const response = await axios.post('/respondent/confirm', {
        respondent_token: token,
        name: respondentInfo.name,
        relationship: respondentInfo.relationship, 
        email: respondentInfo.email,
      });

      if (response.status === 200 || response.status === 201) {
        //setCurrentStep(RESPONDENT_FORM_STEP.COMPLETED);
        navigate(`/respondent/form/${token}`);
      } else {
        throw new Error('APIレスポンスエラー');
      }
    } catch (error: unknown) {
      const axiosError = error as ApiError;
      console.error('Error verify respondent:', axiosError);
      setError('回答者情報の認証中にエラーが発生しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  if (isConflict) {
    return (
      <div className="respondent-form-container">
        <Header user={null} />
        <Container className="blank-content" style={{ minHeight: 'calc(100vh - 300px)'}}>
          <div className="blank-form-container">
            <h4 className="mb-4">お知らせ</h4>

            <div className="text-center">
              <p>
                こちらのリファレンスチェックは既に完了しております。
              </p>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="respondent-form-container">
        <Header user={null} />
        <Container className="blank-content" style={{ minHeight: 'calc(100vh - 300px)'}}>
          <div className="blank-form-container">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">情報を読み込んでいます...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="respondent-form-container">
        <Header user={null} />
        <Container className="blank-content" style={{ minHeight: 'calc(100vh - 300px)'}}>
          <div className="blank-form-container">
            <h4 className="mb-4">エラー発生</h4>

            <div className="text-center">
              <p>{error}</p>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (currentStep === RESPONDENT_FORM_STEP.COMPLETED) {
    return (
      <div className="respondent-form-container">
        <Header user={null} />
        <Container className="blank-content" style={{ minHeight: 'calc(100vh - 300px)'}}>
          <div className="blank-form-container">
            <h4 className="mb-4">回答URLをお送りしました</h4>

            <div className="text-center">
              <p>
                ご本人情報のご入力ありがとうございます。<br />
                ご入力いただいたメールアドレス宛に回答用URLを送信させていただきました。<br />
                受信されたメールよりリファレンスチェックのご回答をお願いいたします。
              </p>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (currentStep === RESPONDENT_FORM_STEP.CONFIRM) {
    return (
      <div className="respondent-form-container">
        <Header user={null} />

        <Container>
          <h4 className="form-title">回答者確認画面</h4>
          <p hidden>入力いただきますメールアドレスへ回答用URLをお送りいたします。</p>

          <Row>
            <Col md={8}>
              <div className="form-container mb-4">
                <div className="form-header">
                  回答者情報
                </div>
                <div className="form-body">
                  <div className="respondent-section">
                    <div className="info-row">
                      <div className="info-label">氏名 <span className="text-danger">*</span></div>
                      <div className="info-value">{respondentInfo.name}</div>
                    </div>

                    <div className="info-row">
                      <div className="info-label">関係性 <span className="text-danger">*</span></div>
                      <div className="info-value">{getRelationshipString(Number(respondentInfo.relationship))}</div>
                    </div>

                    <div className="info-row">
                      <div className="info-label">メールアドレス <span className="text-danger">*</span></div>
                      <div className="info-value">{respondentInfo.email}</div>
                    </div>

                    <div className="info-row">
                      <div className="info-label">利用規約 <span className="text-danger">*</span></div>
                      <div className="info-value">利用規約とプライバシーポリシーに同意する。</div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="confirmation-button-container">
                <Button 
                  variant="success" 
                  className="submit-button"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? '登録中...' : '回答画面へ'}
                </Button>
                <Button 
                  variant="outline-secondary" 
                  onClick={handleBack}
                  className="back-button"
                  disabled={loading}
                >
                  戻る
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="respondent-form-container">
      <Header user={null} />

      <Container>
        <h4 className="form-title">回答者確認画面</h4>
        <p hidden>入力いただきますメールアドレスへ回答用URLをお送りいたします。</p>

        <Row>
          <Col md={8}>
            <div className="form-container mb-4">
              <div className="form-header">
                回答者情報
              </div>
              <div className="form-body">
                <Form onSubmit={handleConfirm}>
                  <div className="respondent-section">
                    <Form.Group className="mb-3">
                      <Form.Label>氏名 <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={respondentInfo.name}
                        onChange={handleInputChange}
                        placeholder="(例) 山田太郎"
                        isInvalid={!!errors.name}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>関係性 <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        name="relationship"
                        value={respondentInfo.relationship}
                        onChange={handleInputChange}
                        isInvalid={!!errors.relationship}
                      >
                        <option value="">選択してください</option>
                        {RELATIONSHIP_OPTIONS.map((option) => (
                          <option key={option.code} value={option.code}>
                            {option.text}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.relationship}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>メールアドレス <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="email"
                        value={respondentInfo.email}
                        onChange={handleInputChange}
                        placeholder="(例) xxxxxxxxxx@xxxxxxx.com"
                        isInvalid={!!errors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>利用規約 <span className="text-danger">*</span></Form.Label>
                      <Form.Check
                        type="checkbox"
                        id="agreeToTerms"
                        label={
                          <>
                            <a href="/terms-of-service" className="legal-link" target="_blank" rel="noopener noreferrer">利用規約</a>
                            と
                            <a href="/privacy-policy" className="legal-link" target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>
                            に同意する。
                          </>
                        }
                        checked={agreeToTerms}
                        onChange={(e) => setAgreeToTerms(e.target.checked)}
                        isInvalid={!!errors.terms}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.terms}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                </Form>
              </div>
            </div>
          </Col>
          <Col md={4}>
            <div className="confirmation-button-container">
              <Button 
                variant="success" 
                className="submit-button"
                onClick={handleConfirm}
                disabled={loading}
              >
                確認画面へ
              </Button>
              <p className="note-text text-left">必須項目を入力してください</p>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default RespondentVerification;
